import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Shield, Check, X, Power } from 'lucide-react';
import apiService from '../../apiService';

const ROLE_LABELS = {
  superadmin: 'Super Admin',
  finance: 'Finance',
  validation: 'Validation & Modération',
  ops_transport: 'Ops Transport',
  ops_delivery: 'Ops Livraison',
  support: 'Support',
  it: 'IT (ingénieur)',
};
const ALL_ROLES = ['finance', 'validation', 'ops_transport', 'ops_delivery', 'support', 'it', 'superadmin'];

const STATUS_BADGE = {
  active: { label: 'Actif', bg: '#ECFDF5', color: '#10B981' },
  inactive: { label: 'Inactif', bg: '#F4F5F7', color: '#5C636E' },
  suspended: { label: 'Suspendu', bg: '#fecaca', color: '#dc2626' },
};

function RoleChips({ roles }) {
  if (!roles || roles.length === 0) return <span style={{ color: '#9AA0A8', fontSize: '13px' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {roles.map(r => (
        <span key={r} style={{
          background: r === 'superadmin' ? '#fef9c3' : '#FEE9E2',
          color: r === 'superadmin' ? '#ca8a04' : '#4338ca',
          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
        }}>{ROLE_LABELS[r] || r}</span>
      ))}
    </div>
  );
}

function CityChips({ admin, cityNames }) {
  if (admin.roles?.includes('superadmin') || admin.all_cities !== false) {
    return <span style={{ background: '#ECFDF5', color: '#10B981', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>Toutes les villes</span>;
  }
  const list = admin.cities || [];
  if (list.length === 0) return <span style={{ color: '#dc2626', fontSize: '11px', fontWeight: 600 }}>Aucune ville</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {list.map(slug => (
        <span key={slug} style={{ background: '#FEE9E2', color: '#C9350F', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>{cityNames[slug] || slug}</span>
      ))}
    </div>
  );
}

function AdminForm({ initial, availableCities, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState(initial?.roles || []);
  const [status, setStatus] = useState(initial?.status || 'active');
  // Cloisonnement par ville : all_cities par défaut (accès global).
  const [allCities, setAllCities] = useState(initial ? (initial.all_cities !== false) : true);
  const [cities, setCities] = useState(initial?.cities || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isSuper = roles.includes('superadmin'); // superadmin → toujours toutes les villes
  const toggleRole = (r) => setRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  const toggleCity = (slug) => setCities(prev => prev.includes(slug) ? prev.filter(x => x !== slug) : [...prev, slug]);

  const submit = async () => {
    setError('');
    if (!isEdit && (!name.trim() || !email.trim() || !password)) return setError('Nom, email et mot de passe requis');
    if (roles.length === 0) return setError('Sélectionnez au moins un rôle');
    if (password && password.length < 8) return setError('Mot de passe : 8 caractères minimum');
    if (!isSuper && !allCities && cities.length === 0) return setError('Sélectionnez au moins une ville, ou activez « Toutes les villes »');
    setSaving(true);
    // Superadmin = toutes les villes ; sinon selon la case.
    const cityScope = isSuper ? { all_cities: true, cities: [] } : { all_cities: allCities, cities: allCities ? [] : cities };
    try {
      if (isEdit) {
        const body = { name, roles, status, ...cityScope };
        if (password) body.password = password;
        const res = await apiService.patch(`/admin/admins/${initial.admin_id}`, body, { userType: 'admin' });
        if (res.success) { onSaved(); onClose(); } else setError(res.error || 'Erreur');
      } else {
        const res = await apiService.post('/admin/admins', { name, email, password, roles, ...cityScope }, { userType: 'admin' });
        if (res.success) { onSaved(); onClose(); } else setError(res.error || 'Erreur');
      }
    } catch (e) {
      setError(e.error || e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflow: 'auto', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{isEdit ? 'Modifier le compte' : 'Nouveau compte de service'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#5C636E' }}>✕</button>
        </div>

        <label style={lbl}>Nom</label>
        <input value={name} onChange={e => setName(e.target.value)} style={inp} placeholder="Nom complet" />

        <label style={lbl}>Email</label>
        <input value={email} onChange={e => setEmail(e.target.value)} disabled={isEdit} type="email" style={{ ...inp, opacity: isEdit ? 0.6 : 1 }} placeholder="email@mbiyo.cd" />

        <label style={lbl}>{isEdit ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe (8 car. min)'}</label>
        <input value={password} onChange={e => setPassword(e.target.value)} type="password" style={inp} placeholder="••••••••" />

        <label style={lbl}>Rôles (cumulables)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
          {ALL_ROLES.map(r => (
            <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px', background: roles.includes(r) ? '#FEE9E2' : '#FFFFFF', border: `1px solid ${roles.includes(r) ? '#c7d2fe' : '#E9EBEF'}` }}>
              <input type="checkbox" checked={roles.includes(r)} onChange={() => toggleRole(r)} />
              <span style={{ fontSize: '14px', fontWeight: r === 'superadmin' ? 700 : 500 }}>{ROLE_LABELS[r]}</span>
              {r === 'superadmin' && <span style={{ fontSize: '11px', color: '#ca8a04' }}>(accès total)</span>}
            </label>
          ))}
        </div>

        {/* Villes d'opération — masqué pour un superadmin (accès global implicite). */}
        {!isSuper && (
          <>
            <label style={lbl}>Villes d'opération</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 10px', borderRadius: '8px', background: allCities ? '#FEE9E2' : '#FFFFFF', border: `1px solid ${allCities ? '#c7d2fe' : '#E9EBEF'}`, marginBottom: '8px' }}>
              <input type="checkbox" checked={allCities} onChange={() => setAllCities(v => !v)} />
              <span style={{ fontSize: '14px', fontWeight: 600 }}>Toutes les villes</span>
              <span style={{ fontSize: '11px', color: '#5C636E' }}>(accès à toutes les activités)</span>
            </label>
            {!allCities && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                {(availableCities || []).length === 0 && (
                  <span style={{ fontSize: '13px', color: '#9AA0A8' }}>Aucune ville disponible</span>
                )}
                {(availableCities || []).map(c => (
                  <label key={c.slug} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px', background: cities.includes(c.slug) ? '#FEE9E2' : '#FFFFFF', border: `1px solid ${cities.includes(c.slug) ? '#FFB900' : '#E9EBEF'}` }}>
                    <input type="checkbox" checked={cities.includes(c.slug)} onChange={() => toggleCity(c.slug)} />
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>{c.name}</span>
                  </label>
                ))}
              </div>
            )}
          </>
        )}

        {isEdit && (
          <>
            <label style={lbl}>Statut</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={inp}>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="suspended">Suspendu</option>
            </select>
          </>
        )}

        {error && <p style={{ color: '#dc2626', fontSize: '13px', margin: '4px 0 12px' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button onClick={onClose} style={{ ...btn, background: '#F4F5F7', color: '#414852' }}>Annuler</button>
          <button onClick={submit} disabled={saving} style={{ ...btn, background: '#4338ca', color: '#fff', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer le compte')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminAccountsPage() {
  const [admins, setAdmins] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/admin/admins', { userType: 'admin' });
      setAdmins(res.admins || []);
      setAvailableCities(res.available_cities || []);
    } catch (e) {
      console.error('load admins:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cityNames = Object.fromEntries((availableCities || []).map(c => [c.slug, c.name]));

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0, color: '#12151A', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Shield size={22} /> Comptes &amp; Rôles
          </h1>
          <p style={{ margin: '4px 0 0', color: '#5C636E', fontSize: '14px' }}>Gérer les accès par service (réservé au Super Admin)</p>
        </div>
        <button onClick={() => { setEditTarget(null); setShowForm(true); }} style={{ ...btn, background: '#4338ca', color: '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <UserPlus size={16} /> Nouveau compte
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', border: '1px solid #E9EBEF' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#FFFFFF', borderBottom: '1px solid #E9EBEF' }}>
              {['Nom', 'Email', 'Rôles', 'Villes', 'Statut', 'Dernière connexion', ''].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#9AA0A8', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '48px', color: '#9AA0A8' }}>Chargement...</td></tr>
            ) : admins.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '48px', color: '#9AA0A8' }}>Aucun compte</td></tr>
            ) : admins.map(a => {
              const st = STATUS_BADGE[a.status] || STATUS_BADGE.inactive;
              return (
                <tr key={a.admin_id} style={{ borderBottom: '1px solid #F4F5F7' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, fontSize: '14px' }}>{a.name}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#5C636E' }}>{a.email}</td>
                  <td style={{ padding: '12px 16px' }}><RoleChips roles={a.roles} /></td>
                  <td style={{ padding: '12px 16px' }}><CityChips admin={a} cityNames={cityNames} /></td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: st.bg, color: st.color, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>{st.label}</span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '12px', color: '#9AA0A8' }}>{a.last_login ? new Date(a.last_login).toLocaleDateString('fr-FR') : '—'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <button onClick={() => { setEditTarget(a); setShowForm(true); }} style={{ ...btn, background: '#FEE9E2', color: '#C9350F', padding: '6px 12px', fontSize: '13px' }}>Modifier</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <AdminForm initial={editTarget} availableCities={availableCities} onClose={() => setShowForm(false)} onSaved={load} />
      )}
    </div>
  );
}

const lbl = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#414852', margin: '10px 0 4px' };
const inp = { width: '100%', padding: '9px 12px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '14px', boxSizing: 'border-box', marginBottom: '4px' };
const btn = { border: 'none', borderRadius: '10px', padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' };
